import React, { useState, useRef } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import ArticleIcon from '@mui/icons-material/Article';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import * as accessoryExcelApi from '../api/accessoryExcelApi';
import { bust } from '../utils/apiCache';
import { useSnackbar } from '../context/SnackbarContext';
import PageHeader from '../components/common/PageHeader';
import ExcelDownloadFilterDialog from '../components/common/ExcelDownloadFilterDialog';

export default function AccessoryExcelImportExport() {
  const [uploading, setUploading] = useState(false);
  const [uploadPhase, setUploadPhase] = useState('idle'); // 'idle' | 'uploading' | 'processing'
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingMaster, setDownloadingMaster] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef();
  const { showSnackbar } = useSnackbar();

  // Filter dialog — shared for both download buttons
  const [filterDialog, setFilterDialog] = useState({ open: false, title: '', target: null });

  const openFilterDialog = (target) => {
    const titles = {
      prices:     'Download Accessory Prices Excel',
      masterData: 'Download Accessory Master Data',
    };
    setFilterDialog({ open: true, title: titles[target], target });
  };

  const handleFilterConfirm = async (filter) => {
    const { target } = filterDialog;
    setFilterDialog((prev) => ({ ...prev, open: false }));

    if (target === 'prices') {
      setDownloading(true);
      try { await accessoryExcelApi.downloadPrices(filter); showSnackbar('Accessory prices Excel downloaded', 'success'); }
      catch (e) { showSnackbar(e.message, 'error'); }
      finally { setDownloading(false); }
    } else if (target === 'masterData') {
      setDownloadingMaster(true);
      try { await accessoryExcelApi.downloadAccessoryMasterData(filter); showSnackbar('Accessory master data downloaded', 'success'); }
      catch (e) { showSnackbar(e.message, 'error'); }
      finally { setDownloadingMaster(false); }
    }
  };

  const handleTemplate = async () => {
    try { await accessoryExcelApi.downloadTemplate(); showSnackbar('Template downloaded', 'success'); }
    catch (e) { showSnackbar(e.message, 'error'); }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) { setSelectedFile(file); setUploadResult(null); }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setUploadPhase('uploading');
    setUploadProgress(0);
    setUploadResult(null);
    try {
      // Step 1: upload file — server responds immediately with { jobId }
      const { jobId } = await accessoryExcelApi.uploadPrices(selectedFile);

      // Step 2: poll until job finishes (max ~5 min, every 2 s)
      setUploadPhase('processing');
      let job = null;
      for (let i = 0; i < 150; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        job = await accessoryExcelApi.pollJob(jobId);
        if (job.status === 'done' || job.status === 'error') break;
      }

      if (!job || job.status !== 'done') {
        throw new Error(job?.error || 'Processing timed out or failed.');
      }

      const result = job.result;
      // The Master Data tabs (Accessories, Accessory Groups, Departments)
      // cache their lists for up to 5 minutes (see utils/apiCache.js) —
      // without busting them here, newly-uploaded accessories exist in the
      // database and are returned by the API, but the Master Data →
      // Accessories tab keeps showing whatever was cached before the
      // upload until that cache naturally expires.
      bust('accessories:');
      bust('accessoryGroups:');
      bust('departments:');
      bust('makes:');
      bust('stats'); // Dashboard's Accessories count card reads this cache
      setUploadResult(result);
      const parts = [`${result.processed} updated`];
      if (result.skipped > 0) parts.push(`${result.skipped} unchanged`);
      if (result.errorCount > 0) parts.push(`${result.errorCount} error(s)`);
      showSnackbar(`Upload complete: ${parts.join(', ')}`, result.errorCount > 0 ? 'warning' : 'success');
    } catch (e) {
      showSnackbar(e.message || 'Upload failed', 'error');
    } finally {
      setUploading(false);
      setUploadPhase('idle');
      setSelectedFile(null);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <Box>
      <PageHeader title="Accessory Excel Import / Export" subtitle="Bulk manage accessory price data using Excel files." />
      <Grid container spacing={3}>
        {/* Download Section */}
        <Grid item xs={12} md={5}>
          <Card>
            <CardHeader
              title="Export Data"
              subheader="Download current accessory prices to Excel"
              avatar={<DownloadIcon color="primary" />}
              titleTypographyProps={{ fontWeight: 600 }}
            />
            <Divider />
            <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Downloads all latest accessory prices. Column order and names follow your configuration settings.
              </Typography>
              <Button
                variant="contained" startIcon={<DownloadIcon />}
                onClick={() => openFilterDialog('prices')} disabled={downloading} fullWidth
              >
                {downloading ? 'Preparing...' : 'Download Accessory Prices Excel'}
              </Button>
              <Button
                variant="outlined" startIcon={<ArticleIcon />}
                onClick={handleTemplate} fullWidth
              >
                Download Blank Template
              </Button>
              <Divider />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Master Data
              </Typography>
              <Button
                variant="outlined" color="success" startIcon={<DownloadIcon />}
                onClick={() => openFilterDialog('masterData')} disabled={downloadingMaster} fullWidth
              >
                {downloadingMaster ? 'Preparing...' : 'Download Accessory Master Data'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Upload Section */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardHeader
              title="Import Data"
              subheader="Upload Excel to create/update accessory prices"
              avatar={<UploadIcon color="secondary" />}
              titleTypographyProps={{ fontWeight: 600 }}
            />
            <Divider />
            <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Alert severity="info" sx={{ fontSize: 13 }}>
                Column headers must match the template format. Download the template for a ready-to-fill format.
                New accessory groups and accessories will be auto-created as needed. Every price change is logged in history.
              </Alert>
              <Box
                sx={{
                  border: '2px dashed #1565C0', borderRadius: 2, p: 3, textAlign: 'center',
                  bgcolor: selectedFile ? '#E3F2FD' : '#FAFAFA', cursor: 'pointer',
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <input type="file" accept=".xlsx" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileChange} />
                <UploadIcon sx={{ fontSize: 40, color: '#1565C0', mb: 1 }} />
                <Typography variant="body2">
                  {selectedFile ? <strong>{selectedFile.name}</strong> : 'Click to select Excel file (.xlsx)'}
                </Typography>
                {selectedFile && (
                  <Typography variant="caption" color="text.secondary">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </Typography>
                )}
              </Box>

              {uploading && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {uploadPhase === 'uploading'
                      ? `Uploading… ${uploadProgress}%`
                      : 'Processing rows in background…'}
                  </Typography>
                  <LinearProgress
                    variant={uploadPhase === 'uploading' ? 'determinate' : 'indeterminate'}
                    value={uploadProgress}
                  />
                </Box>
              )}

              <Button
                variant="contained" color="secondary" startIcon={<UploadIcon />}
                onClick={handleUpload} disabled={!selectedFile || uploading} fullWidth
              >
                {uploading
                  ? (uploadPhase === 'uploading' ? 'Uploading…' : 'Processing…')
                  : 'Upload & Process'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Upload Result */}
        {uploadResult && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <Typography variant="h6" fontWeight={700}>Upload Result</Typography>
                  <Chip icon={<CheckCircleIcon />} label={`${uploadResult.processed} Price${uploadResult.processed === 1 ? '' : 's'} Updated`} color="success" />
                  {uploadResult.accessoriesCreated > 0 && (
                    <Chip icon={<CheckCircleIcon />} label={`${uploadResult.accessoriesCreated} Accessories Added`} color="info" />
                  )}
                  {uploadResult.accessoryGroupsCreated > 0 && (
                    <Chip icon={<CheckCircleIcon />} label={`${uploadResult.accessoryGroupsCreated} Accessory Groups Added`} color="info" />
                  )}
                  {uploadResult.departmentsCreated > 0 && (
                    <Chip icon={<CheckCircleIcon />} label={`${uploadResult.departmentsCreated} Departments Added`} color="info" />
                  )}
                  {uploadResult.makesCreated > 0 && (
                    <Chip icon={<CheckCircleIcon />} label={`${uploadResult.makesCreated} Makes Added`} color="info" />
                  )}
                  {uploadResult.skipped > 0 && (
                    <Chip icon={<RemoveCircleOutlineIcon />} label={`${uploadResult.skipped} Unchanged (skipped)`} color="default" />
                  )}
                  {uploadResult.errorCount > 0 && (
                    <Chip icon={<ErrorIcon />} label={`${uploadResult.errorCount} Errors`} color="error" />
                  )}
                </Box>
                {uploadResult.errors?.length > 0 && (
                  <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Row #</TableCell>
                        <TableCell>Error</TableCell>
                        <TableCell>Data</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {uploadResult.errors.map((err, i) => (
                        <TableRow key={i} sx={{ bgcolor: '#FFF3F3' }}>
                          <TableCell>{err.row}</TableCell>
                          <TableCell sx={{ color: 'error.main' }}>{err.error}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                            {JSON.stringify(err.data)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </Box>
                )}
                {uploadResult.errorCount === 0 && (
                  <Alert severity="success">All rows processed successfully!</Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Filter dialog — uses accessory groups for the Group Code dropdown */}
      <ExcelDownloadFilterDialog
        open={filterDialog.open}
        title={filterDialog.title}
        groupType="accessory"
        onClose={() => setFilterDialog((prev) => ({ ...prev, open: false }))}
        onConfirm={handleFilterConfirm}
      />
    </Box>
  );
}
